<script setup>
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Checkbox } from "@/components/ui/checkbox";
  import { actionSchema } from "@schemas/mCampaignAction";
  import { useMCampaignActionsStore } from "@stores/mCampaignActions";
  import { useMAudiencesStore } from "@stores/mAudiences";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useMCampaignQuotasStore } from "@stores/mCampaignQuotas";
  import { ageOptions, isAdsetWithinAgeRange } from "@utils/ageRanges";
  import { actionFromDetection, isBlankAction } from "@utils/quotaDetection";
  import QuotaDetection from "./QuotaDetection.vue";

  const mCampaignActionsStore = useMCampaignActionsStore();
  const { currentAction, hasBeenSubmitted, adsetGenderOptions, cCampaignOptions, cAdsetOptions, campaignTargetOptions } =
    storeToRefs(mCampaignActionsStore);
  const { actionTriggerOptions, actionTypeOptions, quotaOptions } = mCampaignActionsStore;
  const { activeMAudienceOptions } = storeToRefs(useMAudiencesStore());
  const { cAdsets } = storeToRefs(useCAdsetsStore());
  const { findAnswerQuota } = useMCampaignQuotasStore();

  const needSelectCCampaigns = computed(() =>
    ["pause_c_campaigns", "pause_adsets", "pause_adsets_by_audience"].includes(currentAction.value.action),
  );

  const { values, setFieldValue, validate } = useForm({
    validationSchema: toTypedSchema(actionSchema),
    validateOnMount: hasBeenSubmitted.value,
    initialValues: { ...currentAction.value },
  });

  const activeAdsets = computed(() => cAdsets.value.filter((a) => a.status === "active"));
  const countAdsetsWithinAgeRange = (ageMin, ageMax) =>
    activeAdsets.value.filter((a) => isAdsetWithinAgeRange(a, ageMin, ageMax)).length;

  const matchedAgeAdsetsCount = computed(() => {
    if (values.age_min == null || values.age_max == null) return null;
    return countAdsetsWithinAgeRange(values.age_min, values.age_max);
  });

  watch(values, (v) => Object.assign(currentAction.value, v));
  watch(
    () => values.quota_id,
    (quotaId) => {
      if (!quotaId || !isBlankAction(values)) return;
      const prefill = actionFromDetection(findAnswerQuota(quotaId));
      if (prefill) Object.entries(prefill).forEach(([field, value]) => setFieldValue(field, value));
    },
  );
  watch(hasBeenSubmitted, (v) => v && validate());

  const onTriggerChange = (value) => {
    if (value === "quota_progress" && campaignTargetOptions.value?.length === 1) {
      setFieldValue("survey_target_id", campaignTargetOptions.value[0].value);
    }
  };

  const onActionChange = () => {
    setFieldValue("c_campaigns", []);
    setFieldValue("adsets", []);
    setFieldValue("audiences", []);
    setFieldValue("gender", "all");
    setFieldValue("age_min", null);
    setFieldValue("age_max", null);
  };

  const pluralizeAdsets = (count) => `${count} active ad set${count === 1 ? "" : "s"}`;
</script>

<template>
  <form
    class="space-y-6"
    @submit.prevent>
    <FormField
      v-slot="{ componentField }"
      name="name">
      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="Pause adsets - Genre Femme" />
        </FormControl>
      </FormItem>
    </FormField>
    <section>
      <p class="text-xs font-semibold text-muted-foreground pb-3">IF</p>
      <div class="py-3 space-y-4">
        <div class="grid grid-cols-2 gap-4 items-start">
          <FormField
            v-slot="{ componentField }"
            name="trigger">
            <FormItem>
              <FormLabel>Trigger</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="actionTriggerOptions"
                  @update:model-value="onTriggerChange" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-slot="{ componentField }"
            name="progress">
            <FormItem>
              <FormLabel>Reached</FormLabel>
              <FormControl>
                <Input
                  v-bind="componentField"
                  type="number"
                  min="1" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div
          v-if="['quota_progress', 'target_progress'].includes(values.trigger)"
          class="grid grid-cols-2 gap-4 items-start">
          <FormField
            v-slot="{ componentField }"
            name="survey_target_id">
            <FormItem>
              <FormLabel>Target</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="campaignTargetOptions" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-if="values.trigger === 'quota_progress' && !!values.survey_target_id"
            v-slot="{ componentField }"
            name="quota_id">
            <FormItem>
              <FormLabel>Quota</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="quotaOptions(values.survey_target_id)" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <QuotaDetection
          v-if="values.trigger === 'quota_progress' && values.quota_id"
          :key="values.quota_id"
          :answer-quota-id="values.quota_id" />
      </div>
    </section>
    <section>
      <p class="text-xs font-semibold text-muted-foreground pb-3">THEN</p>
      <div class="py-3 space-y-4">
        <FormField
          v-slot="{ componentField }"
          name="action">
          <FormItem>
            <FormLabel>Action</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="actionTypeOptions"
                @update:model-value="onActionChange" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <div class="grid grid-cols-2 gap-4 items-start">
          <FormField
            v-if="needSelectCCampaigns"
            v-slot="{ componentField }"
            name="c_campaigns">
            <FormItem>
              <FormLabel>Campaigns</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="cCampaignOptions"
                  multiple
                  clearable />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-if="values.action === 'pause_adsets'"
            v-slot="{ componentField }"
            name="adsets">
            <FormItem>
              <FormLabel>Adsets</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="cAdsetOptions"
                  multiple
                  clearable />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-if="values.action === 'pause_adsets_by_audience'"
            v-slot="{ componentField }"
            name="audiences">
            <FormItem>
              <FormLabel>Audiences</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="activeMAudienceOptions"
                  multiple
                  clearable />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-if="values.action === 'pause_adsets_by_gender'"
            v-slot="{ componentField }"
            name="gender">
            <FormItem>
              <FormLabel>Gender</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="adsetGenderOptions" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <template v-if="values.action === 'pause_adsets_by_age_range'">
            <FormField
              v-slot="{ componentField }"
              name="age_min">
              <FormItem>
                <FormLabel>Minimum age</FormLabel>
                <FormControl>
                  <Select
                    v-bind="componentField"
                    :options="ageOptions" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField
              v-slot="{ componentField }"
              name="age_max">
              <FormItem>
                <FormLabel>Maximum age</FormLabel>
                <FormControl>
                  <Select
                    v-bind="componentField"
                    :options="ageOptions" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </template>
        </div>
        <p
          v-if="values.action === 'pause_adsets_by_age_range' && matchedAgeAdsetsCount !== null"
          class="text-xs text-muted-foreground"
          data-cy="action-age-matched-adsets">
          {{ pluralizeAdsets(matchedAgeAdsetsCount) }} entirely within this age range
        </p>
      </div>
    </section>
    <section class="space-y-4">
      <p class="text-sm leading-none font-medium pb-2">Behavior</p>
      <FormField
        v-slot="{ value, handleChange }"
        name="run_only_once">
        <FormItem>
          <div class="flex items-start gap-2">
            <FormControl>
              <Checkbox
                id="run-only-once"
                class="mt-0.5"
                :model-value="!!value"
                @update:model-value="handleChange" />
            </FormControl>
            <div class="space-y-1">
              <Label
                for="run-only-once"
                class="cursor-pointer"
                >Run only once</Label
              >
              <p class="text-xs text-muted-foreground">
                Auto-disabled after the first trigger. Switch it back on to re-arm.
              </p>
            </div>
          </div>
        </FormItem>
      </FormField>
    </section>
  </form>
</template>
